import styled from 'styled-components/macro'
import { Overlay, styleReset } from 'styles/misc'
import { mq } from 'styles/mq'
import { theme } from 'styles/theme'
import GoldenBg from 'assets/golden-bg.webp'

export const MenuToggle = styled.button`
  ${styleReset}
  display: block;
  font-size: 2rem;
  padding: 0;
  cursor: pointer;

  &,
  svg {
    display: block;
    width: 1em;
  }

  ${mq.medium} {
    font-size: 3.5rem;
  }
`

export const Backdrop = styled.button`
  ${styleReset}
  ${Overlay}
  position: fixed;
  -webkit-tap-highlight-color: transparent;
  z-index: 100;
`

export const MenuList = styled.ul<{ inverted: boolean }>`
  --text-color: ${theme.colors.text} !important;
  --background-color: ${theme.colors.background} !important;
  --background: ${theme.colors.background} !important;

  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: max-content;
  padding: calc(var(--header-height) + 4rem) var(--container-spacing) 0 0;
  margin: 0;
  list-style: none;
  font-family: ${theme.fonts.headings};
  transform-origin: calc(100% - 2rem) -1rem;
  transform: translate3d(0, 0, 0);
  transition: transform 0.4s ${theme.ease.out}, opacity 0.2s ${theme.ease.out};
  opacity: 0;
  color: var(--text-color);
  pointer-events: none;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  z-index: 500;
  background: ${theme.colors.background}d9;

  ${mq.smallOnly} {
    padding: calc(var(--header-height) + 4rem) 4rem 0 0;
    width: 100%;
  }

  ${mq.medium} {
    width: 39.3rem;
  }

  button {
    ${styleReset}
    cursor: pointer;
    text-transform: inherit;
  }

  &.enter {
    opacity: 0;
    transform: translateX(10%);
    pointer-events: auto;
  }

  &.enter-active,
  &.enter-done {
    opacity: 1;
    transform: translate3d(0, 0, 0);
    pointer-events: auto;
  }

  &.exit {
    opacity: 1;
    transform: translate3d(0, 0, 0);
    pointer-events: auto;
  }

  &.exit-active {
    opacity: 0;
    transform: translateX(10%);
  }

  ul a,
  ul button {
    position: relative;
    color: inherit;
    text-decoration: none;

    &:hover,
    &:focus-visible,
    &[data-active='true'] {
      &::after {
        content: '';
        display: block;
        position: absolute;
        top: 50%;
        left: 100%;
        transform: translate(60%, -60%);
        width: 1.1rem;
        height: 1.1rem;
        clip-path: path(
          'M11 5.49972L8.93899 6.19635C7.64739 6.633 6.63355 7.64684 6.19691 8.93844L5.50028 10.9994L4.80365 8.93844C4.367 7.64684 3.35316 6.633 2.06156 6.19635L0 5.49972L2.06101 4.80309C3.35261 4.36645 4.36645 3.35261 4.80309 2.06101L5.49972 0L6.19635 2.06101C6.633 3.35261 7.64684 4.36645 8.93844 4.80309L10.9994 5.49972H11Z
'
        );
        background: url(${GoldenBg}) center center / cover no-repeat;
      }
    }
  }

  li:nth-child(2n + 1) ul a,
  li:nth-child(2n + 1) ul button {
    &:hover,
    &:focus-visible,
    &[data-active='true'] {
      &::after {
        left: auto;
        right: 100%;
        transform: translate(-60%, -60%);
      }
    }
  }
`

export const CurrentItem = styled.span`
  position: relative;
  width: min-content;
`

export const Li = styled.li<{ hiddenOnLarge?: boolean }>`
  font-size: 3.2rem;
  font-weight: 700;
  padding: 0.5rem 0 0.5rem 4.5rem;

  a,
  button,
  span,
  ${CurrentItem} {
    display: inline-block;
    height: 1.8em;
    color: inherit;
    text-decoration: none;
    transition: background-color 0.3s;
  }

  a:not(:hover) .circle,
  ${CurrentItem} .circle {
    display: none;
  }

  svg[data-menu-item] {
    display: block;
    margin: -0.2rem -2.6rem 0 0;
  }

  &:nth-child(2n) {
    width: max-content;
    margin-left: auto;
    text-align: right;

    a {
      margin-left: auto;
    }
  }

  ul {
    margin: -1rem 0 0;
    padding: 0;
    font-weight: normal;
    font-size: 2rem;
    list-style: none;

    li {
      padding: 0.2em 0;
    }

    a {
      height: auto;
      padding: 0.5rem 0;
      margin-left: 0;
    }
  }
`

export const MobileMenuItem = styled(Li)`
  display: none;
  counter-increment: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  ${mq.smallOnly} {
    display: block;
  }

  a::before,
  button::before {
    content: '';
  }
`

export const FooterMenuItem = styled(Li)`
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  a::before,
  button::before {
    content: '';
  }
`

export const MobileHidden = styled.span`
  ${mq.smallOnly} {
    display: none;
  }
`

export const MobileOnly = styled.span`
  ${mq.medium} {
    display: none;
  }
`

export const DeviceHidden = styled.span`
  ${mq.ltLarge} {
    display: none;
  }
`

export const DeviceOnly = styled.span`
  ${mq.large} {
    display: none;
  }
`

export const MainMenuItem = styled.a`
  position: relative;
  display: block;
  width: max-content;

  &:is(a)::before {
    --width: 150%;

    content: '';
    display: block;
    position: absolute;
    top: -10%;
    left: calc((var(--width) - 100%) * -1 / 2);
    width: var(--width);
    pointer-events: none;
    background: transparent center center / cover no-repeat;
  }

  &:is(span) {
    opacity: 0.5;
  }

  &:hover {
    text-decoration: none !important;

    &::before {
      background-image: url(${GoldenBg});
    }
  }

  .circles-svg {
    width: 0;
    height: 0;
  }

  &[data-label='Татары']::before {
    aspect-ratio: 384 / 100;
    clip-path: url('#clip-path-main-menu-Татары');
  }

  &[data-label='Коми']::before {
    aspect-ratio: 260 / 97;
    clip-path: url('#clip-path-main-menu-Коми');
  }

  &[data-label='Мордва']::before {
    --width: 140%;

    aspect-ratio: 374 / 117;
    top: -20%;
    clip-path: url('#clip-path-main-menu-Мордва');
  }

  &[data-label='Буряты']::before {
    --width: 140%;

    aspect-ratio: 268 / 98;
    top: -25%;
    clip-path: url('#clip-path-main-menu-Буряты');
  }

  &[data-label='Саха']::before {
    --width: 140%;

    aspect-ratio: 300 / 122;
    top: -18%;
    clip-path: url('#clip-path-main-menu-Саха');
  }
`
